import { useEffect, useRef, useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BadgeCheck, Upload, ShieldAlert, Clock, X } from 'lucide-react';
import { toast } from 'sonner';
import VerifiedBadge from '@/components/VerifiedBadge';

const DOC_TYPES = [
  { v: 'carta_identita', l: "Carta d'identità" },
  { v: 'passaporto', l: 'Passaporto' },
  { v: 'patente', l: 'Patente di guida' },
];

/**
 * Flusso upload documento d'identità per ottenere il badge
 * "Proprietario Verificato". Legge e aggiorna /users/me/identity-status.
 */
export default function IdentityVerificationCard() {
  const [status, setStatus] = useState(null); // { status, doc_type, full_name, ... }
  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState('carta_identita');
  const [fullName, setFullName] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const load = () =>
    api.get('/users/me/identity-status').then(r => setStatus(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!file) { toast.error('Carica una foto del documento'); return; }
    if (!fullName.trim()) { toast.error('Inserisci il nome completo'); return; }
    setBusy(true);
    try {
      // 1. upload file
      const fd = new FormData();
      fd.append('file', file);
      const up = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // 2. submit identity
      await api.post('/users/me/submit-identity', {
        file_id: up.data.file_id,
        doc_type: docType,
        full_name: fullName.trim(),
      });
      toast.success('Documento inviato — in revisione');
      setOpen(false); setFile(null); setFullName('');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const s = status?.status || 'none';

  return (
    <div data-testid="identity-card"
         className="rounded-2xl border border-border bg-card p-6 md:p-8">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="shrink-0 w-12 h-12 rounded-full bg-chartreuse/15 flex items-center justify-center">
          <BadgeCheck size={22} className="text-chartreuse-foreground" style={{ color: 'var(--chartreuse-foreground, #2a6207)' }}/>
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl">Proprietario Verificato</h3>
            {s === 'verified' && <VerifiedBadge size="sm"/>}
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Fai controllare manualmente il tuo documento d'identità dal team AgriNexus. Chi contatta
            i tuoi annunci vede un badge di fiducia che aumenta tasso di risposta e trattative.
          </p>

          {s === 'none' && (
            <div className="mt-5">
              <Button data-testid="identity-start-btn" onClick={() => setOpen(true)} className="rounded-full">
                <Upload size={14} className="mr-2"/> Richiedi la verifica
              </Button>
            </div>
          )}

          {s === 'pending' && (
            <div className="mt-5 flex items-center gap-2 text-sm text-muted-foreground">
              <Clock size={14}/> Documento in revisione — di solito entro 24h lavorative.
            </div>
          )}

          {s === 'verified' && (
            <div className="mt-5 text-sm text-muted-foreground">
              Approvato il {status?.verified_at ? new Date(status.verified_at).toLocaleDateString('it-IT') : '—'}.
              Il badge è ora visibile accanto a tutti i tuoi annunci.
            </div>
          )}

          {s === 'rejected' && (
            <div className="mt-5">
              <div className="flex items-start gap-2 text-sm text-destructive">
                <ShieldAlert size={14} className="mt-0.5"/>
                <div>
                  <div className="font-medium">Documento rifiutato</div>
                  {status?.rejection_reason && <div className="text-muted-foreground">{status.rejection_reason}</div>}
                </div>
              </div>
              <Button className="mt-4 rounded-full" variant="outline" onClick={() => setOpen(true)} data-testid="identity-retry-btn">
                <Upload size={14} className="mr-2"/> Carica un nuovo documento
              </Button>
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-6 pt-6 border-t border-border space-y-4" data-testid="identity-form">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Invia il tuo documento</h4>
            <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={16}/></button>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Tipo di documento</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {DOC_TYPES.map(d => (
                <button key={d.v} type="button" onClick={() => setDocType(d.v)}
                        data-testid={`identity-doc-${d.v}`}
                        className={`rounded-xl border p-3 text-sm text-left ${docType === d.v ? 'border-foreground bg-secondary' : 'border-border'}`}>
                  {d.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Nome e cognome (come sul documento)</Label>
            <Input value={fullName} onChange={e => setFullName(e.target.value)}
                   placeholder="Es. Mario Rossi" data-testid="identity-fullname"/>
          </div>
          <div>
            <Label>Foto del documento (fronte, max 8MB · JPG/PNG/WEBP)</Label>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp"
                     className="hidden" data-testid="identity-file-input"
                     onChange={e => setFile(e.target.files?.[0] || null)}/>
              <Button type="button" variant="outline" className="rounded-full" onClick={() => fileInput.current?.click()}>
                <Upload size={14} className="mr-2"/> {file ? 'Cambia foto' : 'Scegli foto'}
              </Button>
              {file && <span className="text-sm text-muted-foreground truncate max-w-[260px]">{file.name}</span>}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Il documento è visibile <b>solo agli amministratori</b> ai fini della verifica. Non viene
            mostrato a nessun altro utente, viene conservato in archivio criptato e cancellato se cancelli
            l'account (GDPR art. 17). Puoi oscurare numero del documento se preferisci — ci basta nome e
            foto del volto per confrontarli con quelli sul profilo.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={busy || !file || !fullName.trim()} data-testid="identity-submit-btn">
              {busy ? 'Invio…' : 'Invia per la revisione'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
